/**
 * Дашборд загруженности сотрудников. Данные приходят с сервера
 * (GET /reports/workload и /reports/period-capacity), сервер же
 * фильтрует их по scope запрашивающего (computeScope в
 * domain/permissions.ts) — клиент ничего не скрывает сам.
 *
 * Периоды: месяц / квартал / год / произвольный. План — по рабочим дням
 * из производственного календаря (ТК), capacity — норма × ставка.
 * Клик по строке открывает EmployeeTasksModal с задачами сотрудника за
 * выбранный период.
 */
import { useState, useMemo, memo } from "react";
import { SearchBox } from "../SearchBox";
import { SortControl } from "../SortControl";
import { Ic } from "../Icons";
import { ICONS } from "../iconsData";
import WorkloadBar from "../WorkloadBar";
import { useWorkloadDb } from "../../hooks/useDb";
import { useFilters } from "../../hooks/useFilters";
import { useSort } from "../../hooks/useSort";
import { getPrimaryDeptName } from "../../utils/helpers";
import { resolvePeriod } from "../../utils/period";
import { TODAY, iso, parseISO } from "../../utils/date";
import { formatPeriodLabel } from "../../utils/periodLabel";
import { isPartTime, effectiveEmployeeRate } from "../../utils/employeeRate";
import {
  useWorkloadReportQuery,
  usePeriodCapacityQuery,
} from "../../api/queries/reports";

const PERIOD_MODES = [
  { id: "month", label: "Месяц" },
  { id: "quarter", label: "Квартал" },
  { id: "year", label: "Год" },
  { id: "custom", label: "Период" },
];

const SORT_FIELDS = [
  { id: "name", label: "Сотрудник" },
  { id: "dept", label: "Подразделение" },
  { id: "plan", label: "План" },
  { id: "fact", label: "Факт" },
  { id: "util", label: "Загрузка" },
];

const SORT_OPTIONS = SORT_FIELDS.map((s) => ({ value: s.id, label: s.label }));
const NUMERIC_SORTS = new Set(["plan", "fact", "util"]);

const INITIAL_FILTERS = Object.freeze({
  query: "",
  sortField: "name",
  sortDir: "asc",
});

const shiftAnchor = (mode, anchorIso, dir) => {
  const d = parseISO(anchorIso);
  if (mode === "month") d.setMonth(d.getMonth() + dir);
  else if (mode === "quarter") d.setMonth(d.getMonth() + dir * 3);
  else d.setFullYear(d.getFullYear() + dir);
  return iso(d);
};

const employeeLabel = (e) => `${e.last} ${e.first}`;

function WorkloadView({ openEmployeeTasks }) {
  const db = useWorkloadDb();
  const { employees } = db;

  const [mode, setMode] = useState("month");
  const [anchor, setAnchor] = useState(TODAY);
  const [customFrom, setCustomFrom] = useState(TODAY);
  const [customTo, setCustomTo] = useState(TODAY);

  const { filters, setFilter } = useFilters(INITIAL_FILTERS);
  const { query } = filters;

  const {
    field: sortField,
    dir: sortDir,
    handleFieldChange: handleSortFieldChange,
    handleDirToggle: handleSortDirToggle,
  } = useSort({ filters, setFilter, numericFields: NUMERIC_SORTS });

  const { from, to } = useMemo(
    () => resolvePeriod(mode, anchor, customFrom, customTo),
    [mode, anchor, customFrom, customTo],
  );

  const workloadQ = useWorkloadReportQuery(from, to);
  const capacityQ = usePeriodCapacityQuery(from, to);

  const periodCapacity = capacityQ.data ?? {
    workdays: 0,
    hoursPerDay: 8,
    hours: 0,
  };

  const workloadDataRaw = workloadQ.data;
  const workloadData = useMemo(() => workloadDataRaw ?? {}, [workloadDataRaw]);

  const rows = useMemo(() => {
    const empIndex = new Map(employees.map((e) => [e.id, e]));
    const out = [];
    for (const [empId, e] of Object.entries(workloadData)) {
      const employee = empIndex.get(empId);
      if (!employee) continue;
      out.push({
        employee,
        planTotal: e.planTotal,
        factTotal: e.factTotal,
        capacity: e.capacity,
        utilization: e.capacity > 0 ? (e.planTotal / e.capacity) * 100 : 0,
        deptName: getPrimaryDeptName(employee, db),
      });
    }
    return out;
  }, [workloadData, db, employees]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        employeeLabel(r.employee).toLowerCase().includes(q) ||
        r.deptName.toLowerCase().includes(q),
    );
  }, [rows, query]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    const mul = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      let va, vb;
      if (sortField === "plan") {
        va = a.planTotal;
        vb = b.planTotal;
      } else if (sortField === "fact") {
        va = a.factTotal;
        vb = b.factTotal;
      } else if (sortField === "util") {
        va = a.utilization;
        vb = b.utilization;
      } else if (sortField === "dept") {
        va = a.deptName.toLowerCase();
        vb = b.deptName.toLowerCase();
      } else {
        va = employeeLabel(a.employee).toLowerCase();
        vb = employeeLabel(b.employee).toLowerCase();
      }
      if (va < vb) return -1 * mul;
      if (va > vb) return 1 * mul;
      return 0;
    });
    return list;
  }, [filtered, sortField, sortDir]);

  const totals = useMemo(() => {
    let plan = 0,
      fact = 0,
      capacity = 0;
    for (const r of filtered) {
      plan += r.planTotal;
      fact += r.factTotal;
      capacity += r.capacity;
    }
    return {
      plan,
      fact,
      capacity,
      utilization: capacity > 0 ? (plan / capacity) * 100 : 0,
    };
  }, [filtered]);

  const periodLabel = formatPeriodLabel(mode, anchor, from, to);

  const handleRowActivate = (r) => {
    openEmployeeTasks(r.employee.id, from, to);
  };

  const handleRowKeyDown = (r) => (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleRowActivate(r);
    }
  };

  const isLoading = workloadQ.isLoading || capacityQ.isLoading;
  const isError = workloadQ.isError || capacityQ.isError;

  if (isLoading && sorted.length === 0 && !workloadDataRaw) {
    return (
      <div className="rep">
        <div className="rep-panel p-4">
          <div className="rep-panel-title">Загрузка сотрудников</div>
          <div className="empty-note p-4">Загрузка данных…</div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rep">
        <div className="rep-panel p-4">
          <div className="rep-panel-title">Загрузка сотрудников</div>
          <div className="empty-note p-4">
            Не удалось загрузить данные. Попробуйте обновить страницу.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rep">
      <div className="rep-panel p-4">
        <div className="workload-toolbar">
          <div className="seg sm">
            {PERIOD_MODES.map((m) => (
              <button
                type="button"
                key={m.id}
                className={`seg-btn${mode === m.id ? " on" : ""}`}
                onClick={() => setMode(m.id)}
              >
                {m.label}
              </button>
            ))}
          </div>

          {mode !== "custom" ? (
            <div className="cal-nav">
              <button
                type="button"
                className="icon-btn"
                onClick={() => setAnchor(shiftAnchor(mode, anchor, -1))}
              >
                <Ic d={ICONS.left} size={16} />
              </button>
              <div className="workload-period">{periodLabel}</div>
              <button
                type="button"
                className="icon-btn"
                onClick={() => setAnchor(shiftAnchor(mode, anchor, 1))}
              >
                <Ic d={ICONS.right} size={16} />
              </button>
              <button className="btn ghost sm" onClick={() => setAnchor(TODAY)}>
                Сегодня
              </button>
            </div>
          ) : (
            <div className="workload-custom-range">
              <input
                type="date"
                className="inp w-150"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
              />
              <span className="mut">-</span>
              <input
                type="date"
                className="inp w-150"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
              />
            </div>
          )}
        </div>

        <div className="workload-toolbar mt-3">
          <SearchBox
            value={query}
            onChange={(v) => setFilter("query", v)}
            placeholder="Поиск по сотруднику или отделу…"
            className="workload-search"
          />
          <label className="lbl m-0">Сортировка:</label>
          <SortControl
            options={SORT_OPTIONS}
            field={sortField}
            dir={sortDir}
            onFieldChange={handleSortFieldChange}
            onToggleDir={handleSortDirToggle}
            compact
          />
        </div>
      </div>

      <div className="rep-panel p-4">
        <div className="rep-panel-title">
          Загрузка сотрудников · {periodLabel} · {sorted.length}
        </div>
        <div className="workload-period-meta">
          Рабочих дней: <b>{periodCapacity.workdays}</b> · Рабочих часов в
          периоде: <b>{periodCapacity.hours}</b>
        </div>

        {sorted.length === 0 ? (
          <div className="empty-note p-4">
            Нет сотрудников, подходящих под фильтр
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <table className="tbl workload-table">
              <thead>
                <tr>
                  <th>Сотрудник</th>
                  <th>Подразделение</th>
                  <th className="workload-col-bar">План</th>
                  <th className="workload-col-bar">Факт</th>
                  <th className="text-right">Загрузка</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr
                    key={r.employee.id}
                    className="clickable-row"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleRowActivate(r)}
                    onKeyDown={handleRowKeyDown(r)}
                  >
                    <td>
                      <b>{employeeLabel(r.employee)}</b>
                      {isPartTime(r.employee) && (
                        <span className="pos-tag" title="Эффективная ставка">
                          {effectiveEmployeeRate(r.employee)} ст.
                        </span>
                      )}
                    </td>
                    <td className="mut">{r.deptName}</td>
                    <td>
                      <div className="workload-cell">
                        <WorkloadBar
                          value={r.planTotal}
                          max={r.capacity}
                          variant="plan"
                        />
                        <span className="workload-hours">
                          {Math.round(r.planTotal)} ч
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="workload-cell">
                        <WorkloadBar
                          value={r.factTotal}
                          max={r.capacity}
                          variant="fact"
                        />
                        <span className="workload-hours">
                          {Math.round(r.factTotal)} ч
                        </span>
                      </div>
                    </td>
                    <td className="text-right">
                      <span
                        className={`workload-util${r.utilization > 100 ? " over" : ""}`}
                      >
                        {Math.round(r.utilization)}%
                      </span>
                    </td>
                  </tr>
                ))}
                <tr className="workload-total-row">
                  <td>
                    <b>Итого</b>
                  </td>
                  <td className="mut">{sorted.length} сотр.</td>
                  <td>
                    <div className="workload-cell">
                      <WorkloadBar
                        value={totals.plan}
                        max={totals.capacity}
                        variant="plan"
                      />
                      <span className="workload-hours">
                        {Math.round(totals.plan)} ч
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="workload-cell">
                      <WorkloadBar
                        value={totals.fact}
                        max={totals.capacity}
                        variant="fact"
                      />
                      <span className="workload-hours">
                        {Math.round(totals.fact)} ч
                      </span>
                    </div>
                  </td>
                  <td className="text-right">
                    <span
                      className={`workload-util${totals.utilization > 100 ? " over" : ""}`}
                    >
                      {Math.round(totals.utilization)}%
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default memo(WorkloadView);
